/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, Suspense, useMemo } from 'react';
import { Await, useRouteLoaderData } from 'react-router';
import { useAuth } from '@/providers/auth';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { UserMenu } from './user-menu';
import { UserIcon } from '@/components/icons';
// @priceshoes-block-start LOGIN_IDENTIFICATION
import { Link } from '@/components/link';
import { routes } from '@/route-paths';
// @priceshoes-block-end LOGIN_IDENTIFICATION

export default function UserActions(): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const isAuthenticated: boolean = useMemo(() => {
        // Gate on userType only. Under a cached app shell the client restores userType from the
        // `__sfdc_usertype` hint cookie, but customerId is never carried in that hint — gating on it
        // too would wrongly keep a genuinely-registered visitor on the guest branch. userType is
        // authoritative: it is 'registered' iff the JWT carried a registered-customer id.
        return session?.userType === 'registered';
    }, [session]);

    const customerFirstName = useRouteLoaderData<{ customerFirstName?: Promise<string | null> }>(
        'root'
    )?.customerFirstName;

    const accountLabel = tAccount('myAccount');
    const label = !isAuthenticated ? (
        t('affiliate')
    ) : customerFirstName ? (
        <Suspense fallback={accountLabel}>
            <Await resolve={customerFirstName} errorElement={accountLabel}>
                {(firstName: string | null) => (firstName ? t('greeting', { name: firstName }) : accountLabel)}
            </Await>
        </Suspense>
    ) : (
        accountLabel
    );

    const triggerClassName =
        'size-9 gap-2 p-2 has-[>svg]:px-2 lg:h-[34px] lg:w-auto lg:py-1.5 lg:has-[>svg]:px-3 text-sm font-medium text-header-foreground cursor-pointer hover:bg-transparent hover:opacity-50 transition-opacity';

    // @priceshoes-block-start LOGIN_IDENTIFICATION
    // Guests go straight to the sign-in / account activation page from "Afíliate" (PRIC-165 CA1),
    // without the sign-in popover. It is a plain link, so it doesn't announce a popup.
    if (!isAuthenticated) {
        return (
            <Button asChild variant="ghost" className={triggerClassName}>
                <Link to={routes.login} data-testid="user-account-trigger">
                    <UserIcon className="size-5" />
                    <span className="sr-only lg:not-sr-only">{label}</span>
                </Link>
            </Button>
        );
    }
    // @priceshoes-block-end LOGIN_IDENTIFICATION

    // The trigger is a real <button>, not a link. UserMenu wraps it in a PopoverTrigger that
    // stamps aria-haspopup="dialog", so activating it must only open the menu. A <Link> here
    // rendered an <a href> that navigated on click/Enter, changing context on activation of a
    // control that announces itself as a menu opener (WCAG 3.2.2 On Input). The account and
    // sign-in destinations live inside the popover body.
    const trigger = (
        <Button variant="ghost" type="button" className={triggerClassName} data-testid="user-account-trigger">
            <UserIcon className="size-5" />
            <span className="sr-only lg:not-sr-only">{label}</span>
        </Button>
    );

    return <UserMenu isAuthenticated={isAuthenticated} trigger={trigger} />;
}
